import { SettingsSidebar } from "./SettingsSidebar";
import { Outlet, Navigate, useLocation } from "react-router";
import { Direction } from "radix-ui";
import { getSidebarGroups, isPathAvailable } from "./sidebarGroups";
import { useAuth } from "@/Auth";
import { useEffect, useMemo } from "react";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CHAT_PATH } from "@/constants";
import { customToast } from "@/utils/customToast";
import { useDocumentLanguage, useT } from "@/i18n";

export function Settings() {
  const auth = useAuth();
  const location = useLocation();
  const isRoot = Boolean(auth.isAuthenticated && auth.user?.is_root);

  const sidebarGroups = useMemo(() => getSidebarGroups(isRoot), [isRoot]);
  const firstAvailablePath = sidebarGroups[0]?.items[0]?.path;
  const keep = `${location.search}${location.hash}`;

  // Only root users have settings pages; others go back to S, keeping ?logout.
  if (!firstAvailablePath) {
    return (
      <NonAdminRedirect
        to={`${CHAT_PATH}${keep}`}
        signingOut={new URLSearchParams(location.search).has("logout")}
      />
    );
  }
  if (location.pathname === "/settings" || !isPathAvailable(location.pathname, isRoot)) {
    return <Navigate to={`/settings/${firstAvailablePath}${keep}`} replace />;
  }

  return <SettingsLayout groups={sidebarGroups} />;
}

/** The settings pages in the user's S language, right to left for Arabic. */
function SettingsLayout({ groups }: { groups: ReturnType<typeof getSidebarGroups> }) {
  const { t, language, dir } = useT();
  // The page's own `dir` also turns portals (dialogs, menus, sheets) around.
  useDocumentLanguage({ language, dir });

  return (
    <Direction.Provider dir={dir}>
      <TooltipProvider>
        <SidebarProvider
          dir={dir}
          lang={language}
          className="h-svh overflow-hidden bg-background text-foreground"
        >
          <SettingsSidebar groups={groups} />
          <SidebarInset className="min-h-0 overflow-hidden">
            <section
              aria-label={t("settings.page")}
              className="flex min-h-0 flex-1 flex-col overflow-auto"
            >
              <Outlet />
            </section>
          </SidebarInset>
        </SidebarProvider>
      </TooltipProvider>
    </Direction.Provider>
  );
}

/** Sends a non-admin back to S, saying why unless they only came here to sign out. */
function NonAdminRedirect({ to, signingOut }: { to: string; signingOut: boolean }) {
  const { t } = useT();
  const message = t("settings.adminsOnly");
  useEffect(() => {
    if (!signingOut) customToast(message, "error");
  }, [signingOut, message]);
  return <Navigate to={to} replace />;
}
